import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  IconButton,
  Tooltip,
  Switch,
  Stack,
  Divider,
  CardBody,
  CardHeader,
  Heading,
  Card,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaUserTie, FaChalkboardTeacher, FaCalculator, FaFileContract } from 'react-icons/fa';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient';
import { normalizeDocument } from '../../../utils/documentFormat';
import DocumentInput from '../../../components/common/DocumentInput';
import { useSearch } from '../../../contexts/SearchContext';
import EmployeePayrollConfig from '../../../components/payroll/EmployeePayrollConfig';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isPayrollConfigOpen, setIsPayrollConfigOpen] = useState(false);
  const [selectedEmployeeForPayroll, setSelectedEmployeeForPayroll] = useState(null);
  const navigate = useNavigate();
  
  const { searchQuery } = useSearch(); // Usar el contexto de búsqueda

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    documentNumber: '',
    email: '',
    phone: '',
    employeeTypeId: 1, // Default to administrative
    salary: '',
    enabled: true,
    // Condiciones para liquidación
    overtimeRate: '',
    bonusPercentage: '',
    deductionPercentage: '',
    hasHealthInsurance: false,
    hasPensionPlan: false,
    taxExempt: false,
    // Si es docente (replicado)
    isTeacher: false,
    teacherId: null,
    // Campos adicionales
    employeeTitle: '',
    position: '',
    department: '',
    workSchedule: '',
    emergencyContact: '',
    emergencyPhone: '',
    notes: '',
  });

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getData('/employees');
      console.log(response);
      setEmployees(response);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar los empleados',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Cargar empleados al montar el componente
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleCreateEmployee = () => {
    setModalMode('create');
    setSelectedEmployee(null);
    setFormData({
      firstName: '',
      lastName: '',
      documentNumber: '',
      email: '',
      phone: '',
      employeeTypeId: 1,
      salary: '',
      enabled: true,
      overtimeRate: '',
      bonusPercentage: '',
      deductionPercentage: '',
      hasHealthInsurance: false,
      hasPensionPlan: false,
      taxExempt: false,
      isTeacher: false,
      teacherId: null,
      employeeTitle: '',
      position: '',
      department: '',
      workSchedule: '',
      emergencyContact: '',
      emergencyPhone: '',
      notes: '',
    });
    setIsModalOpen(true);
  };

  const handleViewProfile = (employeeId) => {
    navigate(`/admin/employees/${employeeId}`);
  };

  const handleEditEmployee = (employee) => {
    setModalMode('edit');
    setSelectedEmployee(employee);
    setFormData({
      firstName: employee.person?.name || '',
      lastName: employee.person?.lastName || '',
      documentNumber: employee.person?.document || '',
      email: employee.person?.email || '',
      phone: employee.person?.cellPhone || employee.person?.phone || '',
      employeeTypeId: employee.employeeTypeId || 1,
      salary: employee.salary || '',
      enabled: employee.enabled !== undefined ? employee.enabled : true,
      overtimeRate: employee.overtimeRate || '',
      bonusPercentage: employee.bonusPercentage || '',
      deductionPercentage: employee.deductionPercentage || '',
      hasHealthInsurance: employee.hasHealthInsurance || false,
      hasPensionPlan: employee.hasPensionPlan || false,
      taxExempt: employee.taxExempt || false,
      isTeacher: employee.isTeacher || false,
      teacherId: employee.teacherId || null,
      employeeTitle: employee.employeeTitle || '',
      position: employee.position || '',
      department: employee.department || '',
      workSchedule: employee.workSchedule || '',
      emergencyContact: employee.emergencyContact || '',
      emergencyPhone: employee.emergencyPhone || '',
      notes: employee.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este empleado?')) {
      try {
        await deleteData(`/employees/${employeeId}`);
        toast({
          title: 'Éxito',
          description: 'Empleado eliminado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchEmployees();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Error al eliminar el empleado',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleConfigurePayroll = (employee, e) => {
    e.stopPropagation();
    setSelectedEmployeeForPayroll(employee);
    setIsPayrollConfigOpen(true);
  };

  const handleClosePayrollConfig = () => {
    setIsPayrollConfigOpen(false);
    setSelectedEmployeeForPayroll(null);
  };

  const handleSubmit = async () => {
    try {
      const employeeData = {
        ...formData,
        documentNumber: normalizeDocument(formData.documentNumber),
        salary: parseFloat(formData.salary) || 0,
        overtimeRate: parseFloat(formData.overtimeRate) || 0,
        bonusPercentage: parseFloat(formData.bonusPercentage) || 0,
        deductionPercentage: parseFloat(formData.deductionPercentage) || 0,
      };

      if (modalMode === 'create') {
        await postData('/employees', employeeData);
        toast({
          title: 'Éxito',
          description: 'Empleado creado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await putData(`/employees/${selectedEmployee.id}`, employeeData);
        toast({
          title: 'Éxito',
          description: 'Empleado actualizado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setIsModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} el empleado`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getEmployeeTypeBadge = (employeeType, isTeacher) => {
    if (isTeacher) {
      return <Badge colorScheme="blue" leftIcon={<FaChalkboardTeacher />}>Docente</Badge>;
    }
    const typeName = employeeType?.name || employeeType || 'empleado';
    return typeName.toLowerCase().includes('admin') ? 
      <Badge colorScheme="gray" leftIcon={<FaUserTie />}>Administrativo</Badge> :
      <Badge colorScheme="green" leftIcon={<FaUserTie />}>Empleado</Badge>;
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" justify="space-between" mb="20px" align="center">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Gestión de Empleados
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="brand"
            variant="solid"
            onClick={handleCreateEmployee}
          >
            Agregar Empleado
          </Button>
        </Flex>

        {/* Tabla de empleados */}
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Nombre</Th>
              <Th borderColor={borderColor}>Documento</Th>
              <Th borderColor={borderColor}>Email</Th>
              <Th borderColor={borderColor}>Tipo</Th>
              <Th borderColor={borderColor}>Salario Base</Th>
              <Th borderColor={borderColor}>Estado</Th>
              <Th borderColor={borderColor}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {employees.map((employee) => (
              <Tr 
                key={employee.id}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => handleViewProfile(employee.id)}
              >
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {employee.person?.name} {employee.person?.lastName}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {employee.person?.document}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {employee.person?.email}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  {getEmployeeTypeBadge(employee.employeeType?.description, employee.isTeacher)}
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    ${employee.salary?.toLocaleString()}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge colorScheme={employee.enabled ? 'green' : 'red'}>
                    {employee.enabled ? 'Activo' : 'Inactivo'}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap="2">
                    <Tooltip label="Historial de contratos">
                      <IconButton
                        icon={<FaFileContract />}
                        size="sm"
                        colorScheme="teal"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/employee-contracts?employeeId=${employee.id}`);
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Configurar Liquidación">
                      <IconButton
                        icon={<FaCalculator />}
                        size="sm"
                        colorScheme="brand"
                        variant="ghost"
                        onClick={(e) => handleConfigurePayroll(employee, e)}
                      />
                    </Tooltip>
                    <Tooltip label="Eliminar empleado">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEmployee(employee.id);
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {employees.length === 0 && !loading && (
          <Text textAlign="center" py="20px" color="gray.500">
            No se encontraron empleados
          </Text>
        )}
      </Card>

      {/* Modal para crear/editar empleado */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalMode === 'create' ? 'Agregar Empleado' : 'Editar Empleado'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {/* Información Personal */}
              <Card>
                <CardHeader>
                  <Heading size="md">Información Personal</Heading>
                </CardHeader>
                <CardBody>
                  <Stack spacing={4}>
                    <Flex gap={4}>
                      <FormControl>
                        <FormLabel>Nombre</FormLabel>
                        <Input
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          placeholder="Nombre"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Apellido</FormLabel>
                        <Input
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          placeholder="Apellido"
                        />
                      </FormControl>
                    </Flex>
                    <Flex gap={4}>
                      <FormControl>
                        <FormLabel>Documento</FormLabel>
                        <DocumentInput
                          value={formData.documentNumber}
                          onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                          placeholder="Número de documento"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Email</FormLabel>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Email"
                        />
                      </FormControl>
                    </Flex>
                    <FormControl>
                      <FormLabel>Teléfono</FormLabel>
                      <Input
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="Teléfono"
                      />
                    </FormControl>
                  </Stack>
                </CardBody>
              </Card>

              {/* Información Laboral */}
              <Card>
                <CardHeader>
                  <Heading size="md">Información Laboral</Heading>
                </CardHeader>
                <CardBody>
                  <Stack spacing={4}>
                    <Flex gap={4}>
                      <FormControl>
                        <FormLabel>Tipo de Empleado</FormLabel>
                        <Select
                          value={formData.employeeTypeId}
                          onChange={(e) => handleInputChange('employeeTypeId', parseInt(e.target.value))}
                        >
                          <option value={1}>Administrativo</option>
                          <option value={2}>Docente</option>
                          <option value={3}>Otro</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Salario Base</FormLabel>
                        <NumberInput
                          value={formData.salary}
                          onChange={(value) => handleInputChange('salary', value)}
                          min={0}
                        >
                          <NumberInputField placeholder="Salario base" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </Flex>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">Empleado Activo</FormLabel>
                      <Switch
                        isChecked={formData.enabled}
                        onChange={(e) => handleInputChange('enabled', e.target.checked)}
                      />
                    </FormControl>
                  </Stack>
                </CardBody>
              </Card>

              {/* Condiciones para Liquidación */}
              <Card>
                <CardHeader>
                  <Heading size="md" leftIcon={<FaCalculator />}>Condiciones para Liquidación</Heading>
                </CardHeader>
                <CardBody>
                  <Stack spacing={4}>
                    <Flex gap={4}>
                      <FormControl>
                        <FormLabel>Tarifa Extra (por hora)</FormLabel>
                        <NumberInput
                          value={formData.overtimeRate}
                          onChange={(value) => handleInputChange('overtimeRate', value)}
                          min={0}
                        >
                          <NumberInputField placeholder="Tarifa extra" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Bonificación (%)</FormLabel>
                        <NumberInput
                          value={formData.bonusPercentage}
                          onChange={(value) => handleInputChange('bonusPercentage', value)}
                          min={0}
                          max={100}
                        >
                          <NumberInputField placeholder="% de bonificación" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </Flex>
                    <FormControl>
                      <FormLabel>Descuentos (%)</FormLabel>
                      <NumberInput
                        value={formData.deductionPercentage}
                        onChange={(value) => handleInputChange('deductionPercentage', value)}
                        min={0}
                        max={100}
                      >
                        <NumberInputField placeholder="% de descuentos" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <Divider />
                    <Stack spacing={3}>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">Seguro de Salud</FormLabel>
                        <Switch
                          isChecked={formData.hasHealthInsurance}
                          onChange={(e) => handleInputChange('hasHealthInsurance', e.target.checked)}
                        />
                      </FormControl>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">Plan de Pensión</FormLabel>
                        <Switch
                          isChecked={formData.hasPensionPlan}
                          onChange={(e) => handleInputChange('hasPensionPlan', e.target.checked)}
                        />
                      </FormControl>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">Exento de Impuestos</FormLabel>
                        <Switch
                          isChecked={formData.taxExempt}
                          onChange={(e) => handleInputChange('taxExempt', e.target.checked)}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>
                </CardBody>
              </Card>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {modalMode === 'create' ? 'Crear Empleado' : 'Actualizar Empleado'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para configurar liquidación de empleado */}
      <Modal 
        isOpen={isPayrollConfigOpen} 
        onClose={handleClosePayrollConfig} 
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxW="95vw">
          <ModalHeader>
            Configuración de Liquidación - {selectedEmployeeForPayroll?.person?.name} {selectedEmployeeForPayroll?.person?.lastName}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEmployeeForPayroll && (
              <EmployeePayrollConfig 
                key={`payroll-config-${selectedEmployeeForPayroll.id}`}
                preselectedEmployeeId={selectedEmployeeForPayroll.id}
                preselectedEmployeeName={`${selectedEmployeeForPayroll.person?.name} ${selectedEmployeeForPayroll.person?.lastName}`}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
